import React from 'react';
import {
  ChevronLeft, ChevronRight,
  DollarSign, Truck, Package, CheckCircle, CreditCard, AlertCircle,
  ShoppingCart, Receipt, MapPin
} from 'lucide-react';
import { ORDER_STATUS, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, DELIVERY_STATUS, DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS } from '../constants/status';

// 送货单状态图标
const DELIVERY_ICONS = {
  [DELIVERY_STATUS.WAITING_DELIVERY]: Truck,
  [DELIVERY_STATUS.ARRIVED]: MapPin,
  [DELIVERY_STATUS.WAITING_APPROVAL]: AlertCircle,
  [DELIVERY_STATUS.STORED]: Package,
  [DELIVERY_STATUS.PENDING_SETTLEMENT]: DollarSign,
  [DELIVERY_STATUS.SETTLED]: CheckCircle,
  [DELIVERY_STATUS.INVOICED]: Receipt,
  [DELIVERY_STATUS.PAID]: CreditCard,
};

// 进度条组件
const ProgressBar = ({ stats }) => {
  const total = stats.total || 0;
  if (total === 0) return null;

  const completed = stats.paid || 0;
  const percentage = Math.round(completed / total * 100);

  const segments = [
    { key: 'waitingDelivery', color: 'bg-slate-400', count: stats.waitingDelivery },
    { key: 'arrived', color: 'bg-blue-400', count: stats.arrived },
    { key: 'waitingApproval', color: 'bg-amber-400', count: stats.waitingApproval },
    { key: 'stored', color: 'bg-purple-400', count: stats.stored },
    { key: 'pendingSettlement', color: 'bg-orange-400', count: stats.pendingSettlement },
    { key: 'settled', color: 'bg-amber-400', count: stats.settled },
    { key: 'invoiced', color: 'bg-teal-400', count: stats.invoiced },
    { key: 'paid', color: 'bg-green-400', count: stats.paid },
  ];

  return (
    <div className="mt-4">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-slate-600">送货进度</span>
        <span className="font-medium text-slate-800">{completed}/{total} 已付款 ({percentage}%)</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
        {segments.map(seg => (
          seg.count > 0 && (
            <div
              key={seg.key}
              className={`${seg.color} h-full ${seg.key === 'waitingApproval' ? 'animate-pulse' : ''}`}
              style={{ width: `${seg.count / total * 100}%` }}
            />
          )
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
        {stats.waitingDelivery > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" />待送货 {stats.waitingDelivery}</span>}
        {stats.arrived > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />已到场 {stats.arrived}</span>}
        {stats.waitingApproval > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />等待审批 {stats.waitingApproval}</span>}
        {stats.stored > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" />已入库 {stats.stored}</span>}
        {stats.pendingSettlement > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" />待结算 {stats.pendingSettlement}</span>}
        {stats.settled > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />已结算 {stats.settled}</span>}
        {stats.invoiced > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-400" />已开票 {stats.invoiced}</span>}
        {stats.paid > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" />已付款 {stats.paid}</span>}
      </div>
    </div>
  );
};

// 送货单卡片组件
const DeliveryCard = ({ delivery, onClick }) => {
  const statusConfig = DELIVERY_STATUS_COLORS[delivery.status];
  const StatusIcon = DELIVERY_ICONS[delivery.status];
  const isWaitingApproval = delivery.status === DELIVERY_STATUS.WAITING_APPROVAL;

  // 根据状态获取显示信息
  const getStatusInfo = () => {
    const { status, reservation, arrival, storage, settlement, invoice, payment } = delivery;

    switch (status) {
      case DELIVERY_STATUS.WAITING_DELIVERY:
        return {
          primary: { label: '预约时间', value: reservation?.time || '-' },
          secondary: [
            { label: '车牌', value: reservation?.vehicleNo || '-' },
            { label: '司机', value: reservation?.driverName || '-' },
          ],
        };
      case DELIVERY_STATUS.ARRIVED:
        return {
          primary: { label: '到场时间', value: arrival?.at || '-' },
          secondary: [
            { label: '车牌', value: reservation?.vehicleNo || '-' },
            { label: '操作员', value: arrival?.operator || '-' },
          ],
        };
      case DELIVERY_STATUS.WAITING_APPROVAL:
        return {
          primary: { label: '到场时间', value: arrival?.at || '-' },
          secondary: [
            { label: '车牌', value: reservation?.vehicleNo || '-' },
            { label: '等待订单审批', value: '', highlight: true },
          ],
        };
      case DELIVERY_STATUS.STORED:
        return {
          primary: { label: '实重', value: `${storage?.actualWeight || 0} 吨`, highlight: true },
          secondary: [
            { label: '质检', value: storage?.quality || '-' },
            { label: '入库时间', value: storage?.at || '-' },
          ],
          amount: storage?.amountWithTax,
        };
      case DELIVERY_STATUS.PENDING_SETTLEMENT:
        return {
          primary: { label: '实重', value: `${storage?.actualWeight || 0} 吨`, highlight: true },
          secondary: [
            { label: '质检', value: storage?.quality || '-' },
            { label: '待确认结算', value: '', highlight: true },
          ],
          amount: storage?.amountWithTax,
        };
      case DELIVERY_STATUS.SETTLED:
        return {
          primary: { label: '实重', value: `${storage?.actualWeight || 0} 吨`, highlight: true },
          secondary: [
            { label: '结算金额', value: `¥${settlement?.amount?.toLocaleString() || 0}` },
            { label: '确认时间', value: settlement?.confirmedAt || '-' },
          ],
          amount: settlement?.amount,
        };
      case DELIVERY_STATUS.INVOICED:
        return {
          primary: { label: '实重', value: `${storage?.actualWeight || 0} 吨`, highlight: true },
          secondary: [
            { label: '发票号', value: invoice?.no || '-' },
            { label: '开票时间', value: invoice?.at || '-' },
          ],
          amount: invoice?.amount,
        };
      case DELIVERY_STATUS.PAID:
        return {
          primary: { label: '实重', value: `${storage?.actualWeight || 0} 吨`, highlight: true },
          secondary: [
            { label: '付款金额', value: `¥${payment?.amount?.toLocaleString() || 0}` },
            { label: '付款时间', value: payment?.at || '-' },
          ],
          amount: payment?.amount,
        };
      default:
        return {
          primary: { label: '预约时间', value: reservation?.time || '-' },
          secondary: [
            { label: '车牌', value: reservation?.vehicleNo || '-' },
            { label: '司机', value: reservation?.driverName || '-' },
          ],
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div
      onClick={() => onClick(delivery)}
      className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
        isWaitingApproval
          ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
          : 'bg-white border-slate-200 hover:border-blue-200'
      }`}
    >
      {/* 头部：单号和状态 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
            {delivery.deliveryNo}
          </span>
          {isWaitingApproval && (
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded animate-pulse">
              需关注
            </span>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${statusConfig.bg} ${statusConfig.text}`}>
          <StatusIcon size={12} />
          {DELIVERY_STATUS_LABELS[delivery.status]}
        </span>
      </div>

      {/* 主要信息 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">{statusInfo.primary.label}:</span>
          <span className={`text-sm font-medium ${statusInfo.primary.highlight ? 'text-slate-800' : 'text-slate-700'}`}>
            {statusInfo.primary.value}
          </span>
        </div>
        {statusInfo.amount !== undefined && (
          <span className="text-sm font-bold text-red-600">
            ¥{statusInfo.amount?.toLocaleString()}
          </span>
        )}
      </div>

      {/* 次要信息 */}
      <div className="flex items-center gap-4 text-xs text-slate-500">
        {statusInfo.secondary.map((item, index) => (
          item.value ? (
            <span key={index} className={item.highlight ? 'text-amber-600 font-medium' : ''}>
              {item.label}: {item.value}
            </span>
          ) : (
            <span key={index} className="text-amber-600 font-medium">{item.label}</span>
          )
        ))}
      </div>

      {/* 底部 */}
      <div className="flex items-center justify-end mt-2 pt-2 border-t border-slate-100">
        <span className="text-xs text-blue-500 flex items-center gap-1">
          查看详情 <ChevronRight size={14} />
        </span>
      </div>
    </div>
  );
};

// 主组件
export default function OrderDetailView({ order, deliveries, onBack, onDeliveryClick }) {
  if (!order) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">订单不存在</p>
      </div>
    );
  }

  const statusConfig = ORDER_STATUS_COLORS[order.status] || ORDER_STATUS_COLORS[ORDER_STATUS.PENDING_APPROVAL];

  // 计算送货统计
  const deliveryStats = {
    total: deliveries.length,
    waitingDelivery: deliveries.filter(d => d.status === DELIVERY_STATUS.WAITING_DELIVERY).length,
    arrived: deliveries.filter(d => d.status === DELIVERY_STATUS.ARRIVED).length,
    waitingApproval: deliveries.filter(d => d.status === DELIVERY_STATUS.WAITING_APPROVAL).length,
    stored: deliveries.filter(d => d.status === DELIVERY_STATUS.STORED).length,
    pendingSettlement: deliveries.filter(d => d.status === DELIVERY_STATUS.PENDING_SETTLEMENT).length,
    settled: deliveries.filter(d => d.status === DELIVERY_STATUS.SETTLED).length,
    invoiced: deliveries.filter(d => d.status === DELIVERY_STATUS.INVOICED).length,
    paid: deliveries.filter(d => d.status === DELIVERY_STATUS.PAID).length,
  };

  return (
    <div className="space-y-6">
      {/* 返回按钮和标题 */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} className="text-slate-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">订单详情</h2>
          <p className="text-sm text-slate-500">{order.orderNo}</p>
        </div>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${statusConfig.bg || 'bg-slate-100'}`}>
          <span className={`font-medium ${statusConfig.text || 'text-slate-700'}`}>
            {ORDER_STATUS_LABELS.workbench[order.status] || order.status}
          </span>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="space-y-6">
        {/* 等待审批提醒 */}
        {deliveryStats.waitingApproval > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">需要关注</h4>
                <p className="text-sm text-amber-700 mt-1">
                  有 {deliveryStats.waitingApproval} 辆车已到场但订单尚未审批通过，请尽快处理。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 基本信息 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-medium text-slate-700 mb-4 flex items-center gap-2">
            <ShoppingCart size={16} className="text-slate-500" />
            订单信息
          </h3>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">基地</p>
                <p className="font-medium text-slate-800">{order.baseName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">物料</p>
                <p className="font-medium text-slate-800">{order.materialName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">供应商</p>
                <p className="font-medium text-slate-800">{order.supplierName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">预估重量</p>
                <p className="font-medium text-slate-800">{order.estimatedWeight} 吨</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">预估金额</p>
                <p className="font-bold text-red-600">¥{order.estimatedAmount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">创建时间</p>
                <p className="font-medium text-slate-800">{order.createdAt}</p>
              </div>
            </div>

            {/* 送货进度 */}
            {deliveries.length > 0 && (
              <ProgressBar stats={deliveryStats} />
            )}
          </div>

          {/* 送货单列表 */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-700 flex items-center gap-2">
                <Truck size={16} className="text-slate-500" />
                送货单 ({deliveries.length})
              </h3>
            </div>

            {/* 状态统计 */}
            {deliveries.length > 0 && (
              <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-5 md:grid-cols-9 gap-2 text-center">
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.total}</p>
                    <p className="text-xs text-slate-600">总计</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.waitingDelivery}</p>
                    <p className="text-xs text-slate-600">待送货</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.arrived}</p>
                    <p className="text-xs text-slate-600">已到场</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.waitingApproval}</p>
                    <p className="text-xs text-slate-600">待审批</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.stored}</p>
                    <p className="text-xs text-slate-600">已入库</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.pendingSettlement}</p>
                    <p className="text-xs text-slate-600">待结算</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.settled}</p>
                    <p className="text-xs text-slate-600">已结算</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.invoiced}</p>
                    <p className="text-xs text-slate-600">已开票</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-800">{deliveryStats.paid}</p>
                    <p className="text-xs text-slate-600">已付款</p>
                  </div>
                </div>
              </div>
            )}

            {deliveries.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package size={32} className="mx-auto mb-2 text-slate-300" />
                <p>暂无送货单</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {deliveries.map(delivery => (
                  <DeliveryCard
                    key={delivery.id}
                    delivery={delivery}
                    onClick={onDeliveryClick}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
    </div>
  );
}
